import { createElement, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';

interface Props {
  text: string;
  className?: string;
  /** delay between words (s) */
  stagger?: number;
  as?: 'p' | 'h2' | 'h3' | 'blockquote' | 'span';
}

/**
 * Reveals text word-by-word with a soft blur/mask as it scrolls into view
 * (plan §5 CH1). The container is a plain element; only the word spans are
 * motion elements, so there is no dynamic-component footgun. Honors
 * prefers-reduced-motion by rendering the text statically.
 */
export default function WordReveal({ text, className, stagger = 0.045, as = 'p' }: Props) {
  const reduce = useReducedMotion();
  const words = text.split(' ');

  const content: ReactNode = reduce
    ? text
    : words.map((w, i) => (
        <motion.span
          key={i}
          style={{ display: 'inline-block', whiteSpace: 'pre' }}
          initial={{ y: '0.5em', opacity: 0, filter: 'blur(6px)' }}
          whileInView={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * stagger }}
        >
          {w}
          {i < words.length - 1 ? ' ' : ''}
        </motion.span>
      ));

  return createElement(as, { className }, content);
}
